// a page that allow guest member to select plan by location

import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Divider } from '@abyss/web/ui/Divider';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { GuestState } from '../..//store/useGuestStore/guestStore';
import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getGeoLocationFromStorage } from '../../common/PSXHeader/SearchBar/utils';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useGuestAcquisitionStore } from '../../store/useGuestAcquisitionStore';
import { GuestAcquisitionState } from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { useGuestStore } from '../../store/useGuestStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import GuestCoachmark from './common/GuestCoachmark';
import { GuestMultiplePlan } from './GuestMultiplePlan';
import { NullLocationMessageContainer } from './GuestPlanSelection.style';
import {
  GuestLocation,
  isSinglePlan,
} from './selectLocation/GuestLocation.utils';
import { GuestLocationSelect } from './selectLocation/GuestLocationSelect';
import { getBlueGreenGQLApiUrl, isLawwGuest } from './utils';

const PlanSelectionPageBody = ({
  backgroundColor = '$bgHeaderColor',
  height = '100vh',
  children,
}) => (
  <Box
    color={backgroundColor}
    css={{
      minHeight: height,
      margin: 0,
      padding: 0,
    }}
  >
    {children}
  </Box>
);
const LocationSelectionWrapper = styled('section', {
  backgroundColor: '$white',
  margin: 0,
  padding: '30px 20px',
  paddingBottom: '24px',
  width: '100%',
});

const PlanSelectionDivider = () => (
  <Box css={{ borderBottom: 'solid 1px $gray3', padding: 0 }}>
    <Divider color="$bgHeaderColor" height={9} margin={0} />
  </Box>
);
export const GuestPlanSelection = () => {
  const [selectedLocation, setSelectedLocation] = useState<
    GuestLocation | undefined
  >(undefined);
  const psxLocation = getGeoLocationFromStorage();
  const savedLocation = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
  );
  const isLawwGuestExperience = isLawwGuest();

  const { guestPlanSelectorData, setChooseType, chooseType } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
      setChooseType: state.setChooseType,
      chooseType: state.chooseType,
    }))
  );
  const { isNavigatingMaestro, isNavigatingFromJarvis } =
    useGuestAcquisitionStore(
      useShallow((state: GuestAcquisitionState) => ({
        isNavigatingMaestro: state.isNavigatingMaestro,
        isNavigatingFromJarvis: state.isNavigatingFromJarvis,
      }))
    );

  const { delsysCode, source, planName } = guestPlanSelectorData || {};

  // Initialize selectedLocation from sessionStorage or geo location on mount
  // Skip for LAWW guests - they should always choose a location
  useEffect(() => {
    if (isNavigatingMaestro || isNavigatingFromJarvis) return; // don't override location for maestro or jarvis navigation
    if (isLawwGuestExperience) {
      return; // Don't use saved location for LAWW guests
    }

    if (psxLocation && savedLocation) {
      let parsedSavedLocation;
      try {
        parsedSavedLocation = JSON.parse(savedLocation);
      } catch {
        parsedSavedLocation = null;
      }
      if (parsedSavedLocation) {
        const location = {
          ...psxLocation,
          zipcode: psxLocation.zipCode,
          label: psxLocation.name,
        };

        setSelectedLocation(location);
        return;
      }
    }
    if (savedLocation) {
      setSelectedLocation(JSON.parse(savedLocation));
    }
  }, [
    psxLocation?.id,
    psxLocation?.longitude,
    psxLocation?.latitude,
    savedLocation,
    isLawwGuestExperience,
  ]);

  // Clear saved location for LAWW guests on mount
  useEffect(() => {
    if (isLawwGuestExperience && savedLocation) {
      sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
    }
  }, [isLawwGuestExperience]);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const lob = searchParams?.get('planSelectionLob') || '';

  const coverageType = searchParams?.get('coverageType');

  const isDeeplinkWithPlanIdentifier = Boolean(
    delsysCode && source === 'deeplink'
  );

  const isPlanSpecificDeepLink = Boolean(planName);

  const [isFirstTimeUserBannerEnabled, enableUpdatedPSXBannerToSignIn] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_UPDATED_PSX_BANNER_TO_SIGN_IN,
    ]);

  const lagoonMessagingTable = useLagoon('ui-messaging');
  const lagoonUIMessage =
    typeof lagoonMessagingTable === 'function'
      ? lagoonMessagingTable()
      : lagoonMessagingTable;
  const nullLocationMessage = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NULL_LOCATION_MESSAGE,
  })?.message;
  const chooseMedicaidPlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICAID_PLAN,
  })?.message;
  const chooseAPlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_PLAN,
  })?.message;
  const chooseMedicarePlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICARE_PLAN,
  })?.message;
  const chooseALocationText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_LOCATION,
  })?.message;

  const coachmarkContentText =
    find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION
        .TYPE_OF_PLAN_SELECTION_COACHMARK_CONTENT,
    })?.message || '';

  const bannerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .PLAN_SELECTION_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const [, setEducationalContentStatus] = useSaveEducationalContentStatus({
    url: guestBGApiUrl,
  });

  const handleOnClose = () => {
    setEducationalContentStore({
      ...educationalContentStore,
      hasViewedGuestPlanBanner: true,
    });

    setEducationalContentStatus({
      variables: {
        ...educationalContentStore,
        hasViewedContent: educationalContentStore?.hasViewedContent ?? true,
        hasViewedGuestPlanBanner: true,
      },
    });
  };

  const isSinglePlanResult = isSinglePlan(lob, coverageType);

  // Treat deeplink with plan identifier same as single plan - hide plan cards
  const shouldHidePlanCards =
    isSinglePlanResult || isDeeplinkWithPlanIdentifier;

  useEffect(() => {
    if (selectedLocation) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(selectedLocation)
      );
    } else {
      sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (shouldHidePlanCards && !selectedLocation) {
      setChooseType(chooseALocationText);
    } else if (lob === 'CS' && coverageType === 'M') {
      setChooseType(chooseMedicaidPlanText);
    } else if (lob === 'MR') {
      setChooseType(chooseMedicarePlanText);
    } else {
      setChooseType(chooseAPlanText);
    }
  }, [
    selectedLocation,
    isSinglePlanResult,
    chooseALocationText,
    chooseMedicaidPlanText,
    chooseAPlanText,
    chooseMedicarePlanText,
    lob,
    coverageType,
    setChooseType,
  ]);

  // Use white background when message is suppressed for LAWW guests
  const shouldUseWhiteBackground =
    selectedLocation || shouldHidePlanCards || isLawwGuestExperience;

  const showFirstTimeUserBanner =
    isFirstTimeUserBannerEnabled &&
    !isPlanSpecificDeepLink &&
    educationalContentStore &&
    !educationalContentStore?.hasViewedGuestPlanBanner;

  return (
    <PlanSelectionPageBody
      backgroundColor={shouldUseWhiteBackground ? '$white' : '$bgHeaderColor'}
      height={shouldUseWhiteBackground ? 'auto' : '100vh'}
    >
      {showFirstTimeUserBanner && !enableUpdatedPSXBannerToSignIn && (
        <FirstTimeUserBanner
          content={bannerContentText}
          onClose={handleOnClose}
          showLearnMoreButton={false}
        />
      )}
      {showFirstTimeUserBanner && enableUpdatedPSXBannerToSignIn && (
        <GuestCoachmark
          content={coachmarkContentText}
          onClose={handleOnClose}
          position="right"
          showLearnMoreButton={false}
          target="#coachmark-anchor"
          title={chooseType}
        />
      )}
      <LocationSelectionWrapper>
        <GuestLocationSelect
          isDataLoaded={isDataLoaded}
          selectedLocation={selectedLocation}
          setIsDataLoaded={setIsDataLoaded}
          setSelectedLocation={setSelectedLocation}
        />
      </LocationSelectionWrapper>

      {(selectedLocation || !shouldHidePlanCards) && (
        <div>
          {!isLawwGuestExperience && (
            <Divider color="$gray3" height="1px" margin="0" />
          )}
          {selectedLocation && psxLocation ? (
            <div>
              <PlanSelectionDivider />
              <GuestMultiplePlan
                key={`${psxLocation.latitude}-${psxLocation.longitude}`}
                selectedLocation={selectedLocation}
              />
            </div>
          ) : (
            !isLawwGuestExperience && (
              <NullLocationMessageContainer justify="center">
                <Text>{nullLocationMessage}</Text>
              </NullLocationMessageContainer>
            )
          )}
        </div>
      )}
    </PlanSelectionPageBody>
  );
};
